import apiClient from '@/shared/api/client';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface CallFaq {
  q: string;
  a: string;
}

// A knowledge-base entry stored in RetellCallAgent.topicsJson — currently used
// by the 'emergency' template only. One topic covers several phrasings of the
// same question plus a single canned response.
export interface CallTopic {
  topic: string;
  questions: string[];
  response: string;
}

export type CallTemplateKey =
  | 'emergency'
  | 'assist'
  | 'overflow' // legacy key — renders as Front-Desk Assist
  | 'campaign'
  | 'newpatient'
  | 'spanish'
  | 'reschedule'
  | 'referral'
  | 'custom';

// Per-template agent config stored in RetellCallAgent.configJson (mirrors
// backend calls.types.ts). Templates only read the keys they declare.
export interface AgentConfig {
  callbackPromise?: string;
  routingMode?: 'assist_first' | 'overflow' | 'scheduled';
  ringSeconds?: number;
  transferRingSeconds?: number;
  transferableIntents?: string[];
  captureOnlyMode?: boolean;
  phoneProvider?: string;
  insuranceList?: string[];
  smsRecapEnabled?: boolean;
  smsDirectionsEnabled?: boolean;
  urgentAlertEnabled?: boolean;
  hotLeadAlertEnabled?: boolean;
  sameDayAlertEnabled?: boolean;
  emergencyReferralAlertEnabled?: boolean;
  confirmBothOffices?: boolean;
  alertPhones?: string[];
  valueTable?: Record<string, number>;
  saveAttemptEnabled?: boolean;
  saveLine?: string;
  cancellationPolicy?: string;
  spanishCallbackStaff?: boolean;
  procedures?: string[];
  formsLink?: string;
}

export interface CallAgent {
  id: string;
  practiceId: string;
  locationId: string;
  name: string;
  templateKey: CallTemplateKey;
  status: 'draft' | 'active' | 'paused';
  agentId: string | null;
  trackingNumber: string | null;
  fallbackNumber: string | null;
  areaCode: string;
  source: string | null;
  greeting: string;
  hours: string;
  address: string;
  faqsJson: CallFaq[];
  topicsJson: CallTopic[];
  notifyEmails: string[];
  transferNumber: string | null;
  doctorLastName: string;
  languages: string[];
  configJson: AgentConfig;
  isProtected: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CallAgentInput {
  locationId: string;
  name: string;
  templateKey: CallTemplateKey;
  greeting?: string;
  faqsJson?: CallFaq[];
  topicsJson?: CallTopic[];
  notifyEmails?: string[];
  transferNumber?: string | null;
  doctorLastName?: string;
  languages?: string[];
  configJson?: AgentConfig;
  areaCode?: string;
  source?: string | null;
}

export type CallAgentUpdate = Partial<Omit<CallAgentInput, 'locationId'>>;

export type QueueStatus = 'new' | 'in_progress' | 'handled' | 'no_answer';
export type HandledOutcome = 'booked' | 'not_booked' | 'not_reached';
export type LeadTemperature = 'hot' | 'warm' | 'cool';

// Union of the per-template extraction payloads Retell posts in
// call_analysis.custom_analysis_data (mirrors backend calls.types.ts). Only the
// fields present for a given call's template are populated — the rest are undefined.
export interface CallExtraction {
  intent?: string;
  handled_by?: string;
  transfer_attempted?: boolean;
  transfer_result?: string | null;
  priority?: string;
  caller_name?: string;
  callback_number?: string;
  patient_status?: string;
  reason?: string;
  preferred_window?: string;
  insurance_carrier?: string | null;
  sms_consent?: boolean;
  source?: string;
  offer_discussed?: boolean;
  service_interest?: string;
  lead_temperature?: string;
  commitment?: string;
  objection?: string;
  referral_source?: string;
  intake_complete?: boolean;
  callback_language?: string;
  saved?: boolean;
  cancel_reason?: string | null;
  same_day?: boolean;
  appointment_ref?: { patient_name?: string; appt_date?: string; provider?: string };
  referring_office?: string;
  referring_doctor?: string;
  referring_office_email?: string;
  office_callback?: string;
  patient_name?: string;
  patient_dob?: string;
  patient_phone?: string;
  pain_level?: string;
  teeth?: string[];
  procedure?: string;
  urgency?: string;
  radiographs?: string;
  outcome?: string;
  outcomeKind?: string;
  summary?: { caller?: string; callback?: string; reason?: string; action?: string };
  [key: string]: unknown;
}

export interface InboundCall {
  id: string;
  locationId: string;
  agentId: string | null;
  sourceId: string | null;
  retellCallId: string;
  callerNumber: string | null;
  agentName: string | null;
  source: string | null;
  outcome: string;
  outcomeKind: 'success' | 'error' | 'neutral';
  disconnectionReason: string | null;
  intent: string | null;
  handledBy: 'agent' | 'transferred' | 'captured_for_callback' | null;
  priority: 'urgent' | 'high' | 'normal';
  queueStatus: QueueStatus | null;
  handledOutcome: HandledOutcome | null;
  handledAt: string | null;
  leadTemperature: LeadTemperature | null;
  commitment: 'booked_intent' | 'callback_requested' | 'not_committed' | 'not_a_lead' | null;
  callbackLanguage: string | null;
  estimatedValue: number | null;
  summaryJson: {
    caller?: string;
    callback?: string;
    reason?: string;
    action?: string;
    summary?: string;
  };
  extractionJson: CallExtraction;
  recordingUrl: string | null;
  transcript: string | null;
  emailedAt: string | null;
  alertsSentAt: string | null;
  notificationError: string | null;
  receivedAt: string;
  endedAt: string | null;
  updatedAt: string;
}

// All computed from real call records (7-day window) — nothing hard-coded.
export interface CallStats {
  callsHandled: number;
  callbacksCaptured: number;
  callbacksHandled: number;
  selfServed: number;
  transferred: number;
  urgentFlags: number;
  afterHoursAnswered: number;
  bookedIntent: number;
  hotLeads: number;
  estimatedValue: number;
  messagesCaptured: number;
  emergenciesEscalated: number;
  callbacksOpen: number; // all-time, not windowed
  notificationFailures: number;
}

export interface AgentHealthIssue {
  agentId: string;
  agentName: string;
  severity: 'error' | 'warning' | 'info';
  message: string;
}

export type SourceChannel =
  | 'google_ads'
  | 'meta'
  | 'mailer'
  | 'landing_page'
  | 'gbp'
  | 'print'
  | 'other';

export interface CallSource {
  id: string;
  locationId: string;
  agentId: string;
  name: string;
  channel: SourceChannel;
  campaignId: string | null;
  campaignName: string | null;
  trackedNumber: string | null;
  offerName: string;
  offerPrice: string;
  offerTerms: string;
  offerExpiry: string;
  targetService: string;
  status: 'draft' | 'active' | 'paused';
  createdAt: string;
  updatedAt: string;
}

export interface CallSourceInput {
  locationId: string;
  agentId: string;
  name: string;
  channel: SourceChannel;
  campaignId?: string | null;
  offerName?: string;
  offerPrice?: string;
  offerTerms?: string;
  offerExpiry?: string;
  targetService?: string;
}

export type CallSourceUpdate = Partial<Omit<CallSourceInput, 'locationId' | 'agentId'>>;

export interface SourcePerformance extends CallSource {
  calls: number;
  leads: number;
  bookedIntent: number;
  hotLeads: number;
  estimatedValue: number;
  conversionPct: number;
  costPerLead: number | null;
}

export interface AttributionCall {
  id: string;
  receivedAt: string;
  callerNumber: string | null;
  caller: string | null;
  callback: string | null;
  reason: string | null;
  sourceName: string | null;
  serviceInterest: string | null;
  objection: string | null;
  commitment: string | null;
  leadTemperature: LeadTemperature | null;
  patientStatus: string | null;
  estimatedValue: number | null;
  queueStatus: QueueStatus | null;
  outcome: string;
  outcomeKind: 'success' | 'error' | 'neutral';
}

export interface SourceAttribution {
  totals: { calls: number; leads: number; bookedIntent: number; estimatedValue: number };
  sources: SourcePerformance[];
  temperature: { hot: number; warm: number; cool: number; notALead: number };
  hotLeads: AttributionCall[];
  recentCalls: AttributionCall[];
}

export interface TrackedNumber {
  id: string;
  number: string;
  agentName: string | null;
  source: string | null;
  status: 'active' | 'paused' | 'draft' | 'unassigned';
  kind: 'agent' | 'source' | 'unassigned';
  agentStatus: 'active' | 'paused' | 'draft' | null; // the answering agent's status
  isProtected: boolean;
  fallbackNumber: string | null;
}

export interface AvailableNumber {
  id: string;
  number: string;
  agentName: string | null;
  locationId: string;
}

// Practice-wide closure (holiday, office event): while active, every agent at
// the location opens with `message` and uses `callbackPromise` if set.
export interface ClosureOverride {
  id: string;
  locationId: string;
  startDate: string;
  endDate: string;
  message: string;
  callbackPromise: string;
  createdAt: string;
  updatedAt: string;
}

export interface ClosureInput {
  locationId: string;
  startDate: string; // YYYY-MM-DD
  endDate: string; // YYYY-MM-DD
  message: string;
  callbackPromise?: string;
}

export interface CallLogFilters {
  agentId?: string;
  since?: string; // ISO timestamp — calls received at or after
  openCallbacks?: boolean; // only callback-queue items still needing action
}

export interface SyncResult {
  synced: number; // new calls added
  updated: number; // already-logged calls refreshed
  failedAgents: { agentName: string; error: string }[];
}

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

// ── API functions ─────────────────────────────────────────────────────────────

export const callsApi = {
  listAgents: (locationId: string): Promise<CallAgent[]> =>
    apiClient
      .get<{ success: boolean; data: CallAgent[] }>('/calls', { params: { locationId } })
      .then((r) => r.data.data),

  createAgent: (input: CallAgentInput): Promise<CallAgent> =>
    apiClient.post<{ success: boolean; data: CallAgent }>('/calls', input).then((r) => r.data.data),

  updateAgent: (id: string, locationId: string, input: CallAgentUpdate): Promise<CallAgent> =>
    apiClient
      .patch<{
        success: boolean;
        data: CallAgent;
      }>(`/calls/${id}`, input, { params: { locationId } })
      .then((r) => r.data.data),

  activateAgent: (
    id: string,
    locationId: string,
    existingPhoneNumber?: string,
  ): Promise<CallAgent> =>
    apiClient
      .post<{
        success: boolean;
        data: CallAgent;
      }>(`/calls/${id}/activate`, existingPhoneNumber ? { existingPhoneNumber } : {}, {
        params: { locationId },
      })
      .then((r) => r.data.data),

  pauseAgent: (id: string, locationId: string): Promise<CallAgent> =>
    apiClient
      .post<{
        success: boolean;
        data: CallAgent;
      }>(`/calls/${id}/pause`, {}, { params: { locationId } })
      .then((r) => r.data.data),

  deleteAgent: (id: string, locationId: string): Promise<void> =>
    apiClient.delete(`/calls/${id}`, { params: { locationId } }).then(() => undefined),

  getTemplateTopics: (templateKey: CallTemplateKey): Promise<CallTopic[]> =>
    apiClient
      .get<{ success: boolean; data: CallTopic[] }>(`/calls/templates/${templateKey}/topics`)
      .then((r) => r.data.data),

  // ── Sources ──
  listSources: (locationId: string): Promise<CallSource[]> =>
    apiClient
      .get<{ success: boolean; data: CallSource[] }>('/calls/sources', { params: { locationId } })
      .then((r) => r.data.data),

  createSource: (input: CallSourceInput): Promise<CallSource> =>
    apiClient
      .post<{ success: boolean; data: CallSource }>('/calls/sources', input)
      .then((r) => r.data.data),

  updateSource: (id: string, locationId: string, input: CallSourceUpdate): Promise<CallSource> =>
    apiClient
      .patch<{
        success: boolean;
        data: CallSource;
      }>(`/calls/sources/${id}`, input, { params: { locationId } })
      .then((r) => r.data.data),

  activateSource: (id: string, locationId: string): Promise<CallSource> =>
    apiClient
      .post<{
        success: boolean;
        data: CallSource;
      }>(`/calls/sources/${id}/activate`, {}, { params: { locationId } })
      .then((r) => r.data.data),

  pauseSource: (id: string, locationId: string): Promise<CallSource> =>
    apiClient
      .post<{
        success: boolean;
        data: CallSource;
      }>(`/calls/sources/${id}/pause`, {}, { params: { locationId } })
      .then((r) => r.data.data),

  deleteSource: (id: string, locationId: string): Promise<void> =>
    apiClient.delete(`/calls/sources/${id}`, { params: { locationId } }).then(() => undefined),

  getSourceAttribution: (locationId: string): Promise<SourceAttribution> =>
    apiClient
      .get<{
        success: boolean;
        data: SourceAttribution;
      }>('/calls/sources/attribution', { params: { locationId } })
      .then((r) => r.data.data),

  // ── Closures ──
  listClosures: (locationId: string): Promise<ClosureOverride[]> =>
    apiClient
      .get<{ success: boolean; data: ClosureOverride[] }>('/calls/closures', {
        params: { locationId },
      })
      .then((r) => r.data.data),

  createClosure: (input: ClosureInput): Promise<ClosureOverride> =>
    apiClient
      .post<{ success: boolean; data: ClosureOverride }>('/calls/closures', input)
      .then((r) => r.data.data),

  deleteClosure: (id: string, locationId: string): Promise<void> =>
    apiClient.delete(`/calls/closures/${id}`, { params: { locationId } }).then(() => undefined),

  // ── Call log ──
  listCallLog: (
    locationId: string,
    page = 1,
    filters: CallLogFilters = {},
    limit = 20,
  ): Promise<{ data: InboundCall[]; meta: PaginationMeta }> =>
    apiClient
      .get<{ success: boolean; data: InboundCall[]; meta: PaginationMeta }>('/calls/log', {
        params: {
          locationId,
          page,
          limit,
          ...(filters.agentId && { agentId: filters.agentId }),
          ...(filters.since && { since: filters.since }),
          ...(filters.openCallbacks && { openCallbacks: 'true' }),
        },
      })
      .then((r) => ({ data: r.data.data, meta: r.data.meta })),

  updateCallQueue: (
    id: string,
    locationId: string,
    input: { queueStatus?: QueueStatus; handledOutcome?: HandledOutcome | null },
  ): Promise<InboundCall> =>
    apiClient
      .patch<{
        success: boolean;
        data: InboundCall;
      }>(`/calls/log/${id}/queue`, input, { params: { locationId } })
      .then((r) => r.data.data),

  getAgentHealth: (locationId: string): Promise<AgentHealthIssue[]> =>
    apiClient
      .get<{ success: boolean; data: AgentHealthIssue[] }>('/calls/health', {
        params: { locationId },
      })
      .then((r) => r.data.data),

  getStats: (locationId: string): Promise<CallStats> =>
    apiClient
      .get<{ success: boolean; data: CallStats }>('/calls/log/stats', { params: { locationId } })
      .then((r) => r.data.data),

  listNumbers: (locationId: string): Promise<TrackedNumber[]> =>
    apiClient
      .get<{
        success: boolean;
        data: TrackedNumber[];
      }>('/calls/numbers', { params: { locationId } })
      .then((r) => r.data.data),

  listAvailableNumbers: (locationId: string, excludeAgentId?: string): Promise<AvailableNumber[]> =>
    apiClient
      .get<{ success: boolean; data: AvailableNumber[] }>('/calls/numbers/available', {
        params: { locationId, ...(excludeAgentId && { excludeAgentId }) },
      })
      .then((r) => r.data.data),

  updateNumberFallback: (
    phoneNumberId: string,
    locationId: string,
    fallbackNumber: string | null,
  ): Promise<{ id: string; fallbackNumber: string | null }> =>
    apiClient
      .patch<{
        success: boolean;
        data: { id: string; fallbackNumber: string | null };
      }>(`/calls/numbers/${phoneNumberId}`, { fallbackNumber }, { params: { locationId } })
      .then((r) => r.data.data),

  deleteNumber: (phoneNumberId: string, locationId: string): Promise<void> =>
    apiClient
      .delete(`/calls/numbers/${phoneNumberId}`, { params: { locationId } })
      .then(() => undefined),

  syncCalls: (locationId: string): Promise<SyncResult> =>
    apiClient
      .post<{
        success: boolean;
        data: SyncResult;
      }>('/calls/log/sync', null, { params: { locationId } })
      .then((r) => r.data.data),
};
